import { getAxiosInstance } from "../api/axios";
import { useEffect, useMemo, useRef } from "react";
import useRefreshToken from "./useRefreshToken";
import LoadingCount from "../utils/constant/loadingCouter";
import { LocalStorageUtil } from "../utils/LocalStorageUtil";
import { showToast } from "../utils/tools";

const getToken = () => {
  try {
    return JSON.parse(String(LocalStorageUtil.getItem("token"))) || "";
  } catch {
    return "";
  }
};

// Flag to track if a token refresh is in progress
let isRefreshing = false;
// Queue of requests to be executed after token refresh
let requestQueue: Array<(token: string) => void> = [];

// Process all queued requests with the new token
const processQueue = (token: string) => {
  requestQueue.forEach((callback) => callback(token));
  requestQueue = [];
};

const useAxiosPrivate = () => {
  const refresh = useRefreshToken();
  const axiosPrivate = useMemo(() => getAxiosInstance(), []);

  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${getToken()}`;
        }
        LoadingCount.incrementCount();
        return config;
      },
      (error) => {
        LoadingCount.decrementCount();
        return Promise.reject(error);
      }
    );

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => {
        LoadingCount.decrementCount();
        return response;
      },
      async (error) => {
        LoadingCount.decrementCount();
        const prevRequest = error?.config;

        if (
          error?.response?.data?.errorCode === "TOKEN_EXPIRED" &&
          !prevRequest?.sent
        ) {
          prevRequest.sent = true;

          // If token refresh is already in progress, queue this request
          if (isRefreshing) {
            return new Promise((resolve) => {
              requestQueue.push((token: string) => {
                prevRequest.headers["Authorization"] = `Bearer ${token}`;
                resolve(axiosPrivate(prevRequest));
              });
            });
          }

          // Start token refresh process
          isRefreshing = true;

          try {
            const newAccessToken = await refresh();
            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

            // Process queued requests
            processQueue(newAccessToken);

            return axiosPrivate(prevRequest);
          } catch (refreshError) {
            // Clear queue on refresh error
            requestQueue = [];
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [axiosPrivate, refresh]);

  return axiosPrivate;
};

export default useAxiosPrivate;
